import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { CheckIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/CheckIcon'
import { PhoneCallIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/PhoneCallIcon'
import { PhoneIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/PhoneIcon'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { Tooltip } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'
import { TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { RiskAlertPopoverData } from '../hooks/useRiskAlertPopover'

const PhoneSection = styled.View(({ theme }) => ({
  gap: theme.spacing['8px'],
}))

const PhoneRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))

const ClientChartContact = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: theme.spacing['8px'],
}))

const ClientChartPhones = styled.View(({ theme }) => ({
  flex: 1,
  gap: theme.spacing['4px'],
}))

const ClientChartPhoneRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['4px'],
}))

type ContactSectionProps = {
  data: RiskAlertPopoverData
  isClientChartVersion?: boolean
}

const ContactSection: React.FC<ContactSectionProps> = ({ data, isClientChartVersion = false }) => {
  const { colors } = useTheme()
  const intl = useIntl()

  const emergencyContactName = [data.emergencyContact.firstName, data.emergencyContact.lastName]
    .filter(Boolean)
    .join(' ')
  const emergencyContactRelationship =
    data.emergencyContact.relationship ||
    intl.formatMessage({
      defaultMessage: 'Emergency contact',
      description: 'Fallback label when emergency contact relationship is not specified',
    })
  const emergencyContactLabel = emergencyContactName ? (
    <FormattedMessage
      defaultMessage='{phone} · {name} ({relationship})'
      description='Emergency contact info with name'
      values={{
        phone: data.emergencyContact.phone,
        name: emergencyContactName,
        relationship: emergencyContactRelationship,
      }}
    />
  ) : (
    <FormattedMessage
      defaultMessage='{phone} · {relationship}'
      description='Emergency contact info without name'
      values={{ phone: data.emergencyContact.phone, relationship: emergencyContactRelationship }}
    />
  )

  if (isClientChartVersion) {
    if (!data.clientPhone && !data.emergencyContact.phone) return null
    return (
      <ClientChartContact testID={tID('RiskAlertPopover-contact')}>
        <PhoneCallIcon size={24} fillColor={colors.iconDefault} />
        <ClientChartPhones>
          <BodyText
            size={BodyTextSize.SMALL}
            color={colors.textSecondary}
            text={
              <FormattedMessage
                defaultMessage='Dial *67 before to hide your number:'
                description='Tip to hide the provider phone number when calling an at-risk client'
              />
            }
          />
          {data.clientPhone && (
            <ClientChartPhoneRow testID={tID('RiskAlertPopover-client-phone')}>
              <BodyText size={BodyTextSize.SMALL} text={data.clientPhone} />
              {data.voicemailConsent && (
                <View testID={tID('RiskAlertPopover-voicemail-checkmark')}>
                  <Tooltip
                    placement='top'
                    hoverEnabled
                    content={
                      <FormattedMessage
                        defaultMessage='{clientFirstName} provided voicemail consent'
                        description='Tooltip message indicating that the client has provided voicemail consent'
                        values={{ clientFirstName: data.clientFirstName }}
                      />
                    }
                  >
                    <CheckIcon size={16} fillColor={colors.iconSuccess} withCircle />
                  </Tooltip>
                </View>
              )}
            </ClientChartPhoneRow>
          )}
          {data.emergencyContact.phone && (
            <ClientChartPhoneRow testID={tID('RiskAlertPopover-emergency-contact')}>
              <BodyText size={BodyTextSize.SMALL} text={data.emergencyContact.phone} />
              <BodyText
                size={BodyTextSize.SMALL}
                color={colors.textSecondary}
                text={
                  emergencyContactName ? (
                    <FormattedMessage
                      defaultMessage='· {name} ({relationship})'
                      description='Emergency contact name and relationship after their phone number'
                      values={{ name: emergencyContactName, relationship: emergencyContactRelationship }}
                    />
                  ) : (
                    <FormattedMessage
                      defaultMessage='· {relationship}'
                      description='Emergency contact relationship after their phone number'
                      values={{ relationship: emergencyContactRelationship }}
                    />
                  )
                }
              />
            </ClientChartPhoneRow>
          )}
        </ClientChartPhones>
      </ClientChartContact>
    )
  }

  return (
    <PhoneSection>
      <Title
        size={TitleSize.SMALL}
        text={<FormattedMessage defaultMessage='Contact information' description='Phone section header' />}
      />
      {data.clientPhone && (
        <PhoneRow testID={tID('RiskAlertPopover-client-phone')}>
          <PhoneIcon size={16} />
          <BodyText size={BodyTextSize.SMALL} text={data.clientPhone} />
          {data.voicemailConsent && (
            <BodyText
              size={BodyTextSize.CAPTION}
              color={colors.textSecondary}
              text={<FormattedMessage defaultMessage='Can leave voicemail' description='Voicemail consent indicator' />}
            />
          )}
        </PhoneRow>
      )}
      {data.emergencyContact.phone && (
        <PhoneRow testID={tID('RiskAlertPopover-emergency-contact')}>
          <PhoneIcon size={16} />
          <BodyText size={BodyTextSize.SMALL} text={emergencyContactLabel} />
        </PhoneRow>
      )}
    </PhoneSection>
  )
}

export default ContactSection
